import clsx from 'clsx'
import {isEmpty, uniqueId} from 'lodash'
import React, {useCallback, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {ShowAddress} from '../../../../_gori/components'
import {TABLE_KEY_COLUMN} from '../../../../_gori/constants'
import {
  convertCurrency,
  convertUserTimeZone,
  formatOmitLongText,
  getShipmentFees,
  KTSVG,
  setLoadingActionTable,
} from '../../../../_gori/helpers'
import {blockPopUp} from '../../../../_gori/helpers'
import {useStatesGlobal} from '../../../../_gori/helpers/components/StatesGlobalProvider'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import {PackageInfoModal, TrackingDetailModal} from '../../orders'
import {TrackingNumbersModal} from '../../shipments'
import {
  Button,
  CarrierBadge,
  Table,
  TableBody,
  TableWrapper,
} from '../../../../_gori/partials/widgets'
import {ParcelBadge} from '../../../../_gori/partials/widgets/mixed/ParcelBadge'
import {useAuth} from '../../auth'
import {ShipmentRefund, ShipmentRefundLabel} from '../../shipments/core/_const'
import ShipmentService from '../../shipments/core/_requests'
import {CopyToClipboard} from 'react-copy-to-clipboard'
import {getParcelCount, getTotalWeight, getWeightUnit} from '../../../../_gori/utils'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {isCreatedWithinLast30Days} from '../core/_helpers'

type Props = {
  tableData: any
  tableClassName?: string
  visibleColumns?: string[]
}

const MAX_LENGTH_TABLE_SCROLL = 5

const ShipmentTable: React.FC<Props> = ({tableData, tableClassName, visibleColumns}) => {
  const intl = useIntl()
  const {currentUser} = useAuth()
  const {statesGlobal} = useStatesGlobal()
  const carriers = statesGlobal.carriers
  const {newCancelToken, isCancel} = useCancelToken()
  const [shipments, setShipments] = useState<any>(tableData)
  const [showModal, setShowModal] = useState<{
    trackingDetail: boolean
    trackingNumbers: boolean
    packageInfoTable: boolean
  }>({
    trackingDetail: false,
    trackingNumbers: false,
    packageInfoTable: false,
  })
  const [dataModal, setDataModal] = useState<any>({
    trackingDetail: {},
    trackingNumbers: null,
    packageInfoTable: null,
  })

  const isTableScroll = useMemo(() => {
    return shipments.length > MAX_LENGTH_TABLE_SCROLL
  }, [shipments.length])

  const handlePrintLabel = useCallback(
    async (shipment: any) => {
      setShipments(setLoadingActionTable(shipments, [shipment.id], true))
      const data = {
        shipments: [
          {
            gori_shipment_id: shipment.gori_shipment_id,
            label: shipment.label.path,
          },
        ],
      }
      await ShipmentService.print(data, {cancelToken: newCancelToken()})
        .then((response) => {
          if (blockPopUp(response.label_url)) {
            toast.warning(
              intl.formatMessage({
                id: 'POP_UP_IS_BLOCKED',
              })
            )
          }
        })
        .catch((error: any) => {
          if (isCancel(error)) return
        })
        .finally(() => {
          setShipments(setLoadingActionTable(shipments, [shipment.id], false))
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isCancel, newCancelToken, intl, shipments]
  )

  const addOnServices = statesGlobal.addOnServices

  const optionsSignature = useMemo(() => {
    return [
      {label: intl.formatMessage({id: 'NONE'}), value: null},
      ...(addOnServices?.signatures || []),
    ]
  }, [addOnServices?.signatures, intl])

  const optionsExtraService = useMemo(() => {
    const options =
      addOnServices?.extra_services?.map((item) => ({
        ...item,
        value: item.value?.toString(),
      })) || []
    return [{label: intl.formatMessage({id: 'NONE'}), value: null}, ...options]
  }, [addOnServices?.extra_services, intl])

  const renderItem = (text?: string) => {
    if (!text) return null

    return (
      <div className='d-flex align-items-center justify-content-start px-2 py-1 rounded-2 fw-bolder text-black bg-mint-gray'>
        <KTSVG path='/media/gori/orders/check.svg' className='svg-icon me-2 text-black' />
        <span>{text}</span>
      </div>
    )
  }

  const columns = useMemo(() => {
    const allColumns = [
      {
        id: TABLE_KEY_COLUMN.DATE,
        Header: intl.formatMessage({id: 'DATE'}),
        headerClassName: 'min-w-100px text-start',
        cellClassName: 'text-start',
        Cell: ({row}: {row: any}) => {
          return convertUserTimeZone(
            row.original?.created_at,
            currentUser,
            process.env.REACT_APP_DATE_FORMAT
          )
        },
      },
      {
        id: TABLE_KEY_COLUMN.SERVICE,
        Header: intl.formatMessage({id: 'SERVICE'}),
        headerClassName: 'min-w-250px text-start',
        cellClassName: 'min-w-250px text-start',
        Cell: ({row}: {row: any}) => {
          let service = carriers?.[row.original.carrier + '_' + row.original.service] ?? []
          let sum = 0
          if (row.original?.fees) {
            sum = getShipmentFees(row.original?.fees?.items)
          }

          return (
            <div>
              <CarrierBadge
                service={service}
                cost={sum}
                alignItem='justify-content-start'
                isInline
              />
            </div>
          )
        },
      },
      {
        id: TABLE_KEY_COLUMN.SHIP_TO,
        Header: intl.formatMessage({id: 'SHIP_TO'}),
        headerClassName: 'min-w-120px text-start',
        cellClassName: 'min-w-120px text-wrap text-start',
        Cell: ({row}: {row: any}) => {
          return <ShowAddress data={row.original} name='to' className='fs-7' />
        },
      },
      {
        id: TABLE_KEY_COLUMN.OUT_FOR_DELIVERY,
        Header: intl.formatMessage({id: 'OUT_FOR_DELIVERY'}),
        headerClassName: 'min-w-150px text-start',
        cellClassName: 'text-start',
        Cell: (data) => {
          const row = data.row
          const ShipmentTracking = row.original?.shipment_tracking ?? null
          const ShipmentTrackingStatus = row.original?.status_label ?? null
          return (
            <div>
              {ShipmentTrackingStatus ? (
                <div className='d-flex flex-column align-items-start'>
                  <span className='fw-bolder'>{ShipmentTrackingStatus}</span>
                  <br />
                  {ShipmentTracking && (
                    <span
                      className='cursor-pointer text-decoration-underline text-muted mt-4'
                      onClick={() => {
                        setShowModal((prev) => ({...prev, trackingDetail: true}))
                        setDataModal((prev) => ({...prev, trackingDetail: ShipmentTracking}))
                      }}
                    >
                      {intl.formatMessage({id: 'SEE_DETAIL'})}
                      {'>'}
                    </span>
                  )}
                </div>
              ) : (
                ''
              )}
            </div>
          )
        },
      },
      {
        id: TABLE_KEY_COLUMN.SHIP_FROM,
        Header: intl.formatMessage({id: 'SHIP_FROM'}),
        headerClassName: 'min-w-120px text-start',
        cellClassName: 'min-w-120px text-wrap text-start',
        Cell: ({row}: {row: any}) => {
          return <ShowAddress data={row.original} name='from' className='fs-7' />
        },
      },
      {
        id: TABLE_KEY_COLUMN.PACKAGE_INFO,
        Header: intl.formatMessage({id: 'PACKAGE_INFO'}),
        headerClassName: 'min-w-120px text-start',
        cellClassName: 'text-start',
        Cell: ({row}: {row: any}) => {
          const trackingNumbers = row.original?.tracking_numbers ?? null
          const isMultiParcel = trackingNumbers && row.original?.tracking_numbers.length > 1

          // Obtain the original parcels information
          const countOriginalParcels = getParcelCount(row.original?.parcels)
          const totalOriginalWeight = getTotalWeight(row.original?.parcels)
          const originalWeightUnit = getWeightUnit(row.original?.parcels)

          return (
            <>
              {isMultiParcel && (
                <div className='badge badge-light'>
                  <span
                    className='cursor-pointer mx-autofw-bold fs-8 bagde badge-light py-1 px-3 rounded-1 text-lowercase lh-sm'
                    onClick={() => {
                      setDataModal((prev) => ({
                        ...prev,
                        packageInfoTable: row.original.parcels,
                      }))
                      setShowModal((prev) => ({...prev, packageInfoTable: true}))
                    }}
                  >
                    {countOriginalParcels} {intl.formatMessage({id: 'PACKAGES'})} <br />
                    <span>
                      {totalOriginalWeight +
                        ' ' +
                        originalWeightUnit +
                        ' ' +
                        intl.formatMessage({id: 'TOTAL'})}
                    </span>
                  </span>
                </div>
              )}
              {!isMultiParcel &&
                row.original.parcels.map((parcel: any) => (
                  <ParcelBadge key={uniqueId('parcel')} className={'bg-white'} values={parcel} />
                ))}
            </>
          )
        },
      },
      {
        id: TABLE_KEY_COLUMN.TRACKING_NUMBER,
        Header: intl.formatMessage({id: 'TRACKING_NUMBER'}),
        headerClassName: 'min-w-180px text-start',
        cellClassName: 'text-wrap text-start',
        Cell: ({row}: {row: any}) => {
          let service = carriers?.[row.original.carrier + '_' + row.original.service] ?? []
          const trackingNumbers = row.original?.tracking_numbers ?? null
          const isMultiParcel = trackingNumbers && row.original?.tracking_numbers.length > 1

          return (
            <>
              {isMultiParcel && (
                <span
                  className='text-primary cursor-pointer'
                  onClick={() => {
                    setShowModal((prev) => ({...prev, trackingNumbers: true}))
                    setDataModal((prev) => ({
                      ...prev,
                      trackingNumbers: row.original,
                    }))
                  }}
                >
                  {intl.formatMessage({id: 'TRACKING_NUMBERS'})}
                  <KTSVG path='/media/gori/orders/external_link.svg' className='svg-icon ps-1' />
                </span>
              )}
              {!isMultiParcel && (
                <div className={'d-flex justify-content-start align-items-center'}>
                  <span
                    className='text-primary cursor-pointer'
                    title={row.original?.tracking_number}
                    onClick={() => {
                      if (
                        blockPopUp(service?.visuals?.tracking_url + row.original.tracking_number)
                      ) {
                        toast.warning(
                          intl.formatMessage({
                            id: 'POP_UP_IS_BLOCKED',
                          })
                        )
                      }
                    }}
                  >
                    {formatOmitLongText(row.original?.tracking_number, 13)}
                  </span>
                  <CopyToClipboard
                    text={row.original?.tracking_number}
                    onCopy={() => toast.success(intl.formatMessage({id: 'COPIED'}))}
                  >
                    <div>
                      <div className='btn btn-active-color-primary btn-icon btn-sm btn-outline-light'>
                        <KTSVG path='/media/gori/account/copy.svg' className='svg-icon' />
                      </div>
                    </div>
                  </CopyToClipboard>
                </div>
              )}
            </>
          )
        },
      },
      {
        id: TABLE_KEY_COLUMN.ADD_ON_SERVICES,
        Header: intl.formatMessage({id: 'ADD_ON_SERVICES'}),
        headerClassName: 'min-w-200px text-start',
        cellClassName: 'text-center ',
        Cell: ({row}: {row: any}) => {
          const amountInsurance = row.original?.fees?.items?.find(
            (item) => item.type === 'gori_insurance'
          )?.amount

          const signatureLabel = optionsSignature.find(
            (item) => item.value === row.original?.signature_confirmation
          )?.label

          const hazmatLabel = optionsExtraService.find(
            (item) => item.value === row.original?.extra_service
          )?.label

          const items = [
            `${intl.formatMessage({
              id: 'INSURANCE',
            })}: ${amountInsurance ? convertCurrency(amountInsurance) : '$0.00'}`,
            row.original?.signature_confirmation ? signatureLabel : null,
            row.original?.extra_service ? hazmatLabel : null,
          ]

          return (
            <div className='d-flex flex-column gap-1 align-items-start'>
              {items.map((item, index) => (
                <div key={index}>{renderItem(item)}</div>
              ))}
            </div>
          )
        },
      },
      {
        id: TABLE_KEY_COLUMN.ACTIONS,
        Header: intl.formatMessage({id: 'ACTIONS'}),
        headerClassName: 'text-start',
        cellClassName: 'text-center',
        Cell: ({row}: {row: any}) => {
          const isPrintLabelAction =
            !row.original.refund &&
            (ShipmentRefundLabel[row.original.refund?.status] ?? 'PRINT_LABEL') === 'PRINT_LABEL'

          const isOver30Days = !isCreatedWithinLast30Days(row.original.created_at)

          return (
            <div
              className={`shipment-refund-badge-${row?.original?.refund?.status} py-2 rounded-pill`}
            >
              {!isEmpty(row.original.refund) ? (
                <span>
                  {intl.formatMessage({
                    id: ShipmentRefundLabel[row.original.refund?.status] ?? 'PRINT_LABEL',
                  })}
                </span>
              ) : isPrintLabelAction ? (
                isOver30Days ? (
                  <OverlayTrigger
                    placement='bottom'
                    overlay={
                      <Tooltip id='tooltip-disabled' className='tooltip-no-max-width'>
                        <p
                          className='mb-0'
                          style={{maxWidth: '300px', textAlign: 'justify'}}
                          dangerouslySetInnerHTML={{
                            __html: intl.formatMessage({id: 'REPRINT_LABEL_4'}),
                          }}
                        />
                      </Tooltip>
                    }
                  >
                    <div className='fw-bolder text-teal-muted d-flex gap-2 cursor-no-drop align-items-center justify-content-center'>
                      <span>
                        {intl.formatMessage({
                          id: 'PRINT',
                        })}
                      </span>
                      <KTSVG
                        path='/media/gori/orders/print.svg'
                        className='svg-icon-2'
                        small={false}
                      />
                    </div>
                  </OverlayTrigger>
                ) : (
                  <div
                    className='fw-bolder text-hover-primary text-teal-clicked d-flex gap-2 cursor-pointer align-items-center justify-content-center'
                    onClick={() => handlePrintLabel(row.original)}
                  >
                    <span>
                      {intl.formatMessage({
                        id: 'PRINT',
                      })}
                    </span>
                    <KTSVG
                      path='/media/gori/orders/print.svg'
                      className='svg-icon-2'
                      small={false}
                    />
                  </div>
                )
              ) : (
                <div
                  className={clsx('btn-group', {
                    'cursor-no-drop': row.original.isLoading,
                  })}
                >
                  <Button
                    className={clsx('btn btn-sm', {
                      'btn-advanced-primary': ![
                        ShipmentRefund.SUBMITTED,
                        ShipmentRefund.REFUNDED,
                        ShipmentRefund.REJECTED,
                      ].includes(row.original.refund?.status),
                      'pe-none': row.original.isLoading,
                      'btn-light-danger': row.original.refund?.status === ShipmentRefund.SUBMITTED,
                      'btn-light-primary': row.original.refund?.status === ShipmentRefund.REFUNDED,
                      'btn-light-info': row.original.refund?.status === ShipmentRefund.REJECTED,
                    })}
                    event={
                      row.original.refund === null ? () => handlePrintLabel(row.original) : null
                    }
                  >
                    {intl.formatMessage({
                      id: ShipmentRefundLabel[row.original.refund?.status] ?? 'PRINT_LABEL',
                    })}
                  </Button>
                </div>
              )}
            </div>
          )
        },
      },
    ]
    if (!visibleColumns) return allColumns
    return allColumns.filter((col) => visibleColumns.includes(col.id ?? ''))
  }, [
    carriers,
    currentUser,
    handlePrintLabel,
    intl,
    optionsExtraService,
    optionsSignature,
    visibleColumns,
  ])

  return (
    <>
      {showModal.trackingDetail && (
        <TrackingDetailModal
          show={showModal.trackingDetail}
          handleClose={() => setShowModal((prev) => ({...prev, trackingDetail: false}))}
          data={dataModal.trackingDetail}
        />
      )}
      {showModal.trackingNumbers && (
        <TrackingNumbersModal
          show={showModal.trackingNumbers}
          handleClose={() => setShowModal((prev) => ({...prev, trackingNumbers: false}))}
          data={dataModal.trackingNumbers}
        />
      )}
      {showModal.packageInfoTable && (
        <PackageInfoModal
          show={showModal.packageInfoTable}
          data={dataModal.packageInfoTable}
          handleClose={() => setShowModal((prev) => ({...prev, packageInfoTable: false}))}
        />
      )}
      <div className={clsx(tableClassName)}>
        <TableWrapper className='bg-white rounded' fullWidth={false}>
          <TableBody
            height={isTableScroll ? 'calc(100vh - 240px)' : 'auto'}
            minHeight={isTableScroll ? 'undefined' : 'auto'}
          >
            <div className='fs-3 fw-bolder my-6'>{intl.formatMessage({id: 'SHIPMENTS'})}</div>
            <Table
              columns={columns}
              data={shipments}
              tableClass='shipment-table'
              theadClass='text-gray-600 fw-bold bg-light'
              tbodyClass='text-gray-600 fw-bold'
              usePagination={false}
              useCheckBox={false}
            />
          </TableBody>
        </TableWrapper>
      </div>
    </>
  )
}

export {ShipmentTable}
